import { useNavigate, useLocation } from 'react-router';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldSet } from '@/components/ui/field';
import ArrowleftIcon from '@/components/svg/ArrowleftIcon';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';
import { useCustomParam } from '@/hooks';
import { useSendEmailVerificationCode, useStartUserEmailAuthSession } from '@/features/auth/mutations';
import { useState } from 'react';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { ArrowUpRight, CheckIcon } from 'lucide-react';

const VerifyCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const getParam = useCustomParam();
  const rid = getParam('rid');
  const referrer = getParam('referrer');
  const username = getParam('username');
  const ridOrRef = rid || referrer;
  const email = (location.state as { email?: string })?.email;

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [codeResent, setCodeResent] = useState(false);
  const [manualRedirectUrl, setManualRedirectUrl] = useState<string | null>(null);

  const sendEmailVerificationCode = useSendEmailVerificationCode();
  const startUserEmailAuthSession = useStartUserEmailAuthSession();

  const handleNavigateBack = () => {
    return navigate(location.pathname + location.search, { 
      state: { view: 'email' },
      replace: true 
    });
  };

  const handleResendCode = () => {
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    sendEmailVerificationCode.mutate(
      { email: email, username: username || null },
      {
        onSuccess() {
          setError('');
          setManualRedirectUrl(null);
          setCodeResent(true);
          setTimeout(() => {
            setCodeResent(false);
          }, 3000);
        },
        onError(error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'There was an error resending the verification code. Please contact us at contact@getcollectr.com';
          setError(errorMessage);
        },
      }
    );
  };

  const handleSubmit = async () => {
    if (!ridOrRef) {
      setError('Referrer or rid is required');
      return;
    }
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    startUserEmailAuthSession.mutate(
      {
        code,
        email,
        username: username || null,
        referrer: ridOrRef || null,
      },
      {
        onSuccess: (data, variables) => {
          setError('');
          setCode('');

          setTimeout(() => {
            let redirectUrl = '';

            if (variables.referrer?.indexOf('?') != -1 || variables.referrer?.indexOf('&') != -1) {
              redirectUrl = variables.referrer + `&token=${data.token}&username=${data.username}`;
            } else {
              redirectUrl = variables.referrer + `?token=${data.token}&username=${data.username}`;
            }
          
            setManualRedirectUrl(redirectUrl);
          }, 2000);
        },
        onError: (error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : 'Code is invalid or expired. Please resend the code. If this persists, please contact us at contact@getcollectr.com';
          setError(errorMessage);
          setCode('');
        },
      }
    );
  };


  return (
    <div className='flex flex-col justify-center my-auto min-h-screen'>
      <Card className='sm:max-w-md w-full mx-auto sm:min-h-min min-h-screen sm:rounded-xl rounded-none'>
        <CardHeader>
          <div className="flex justify-between">
            <div className="mx-auto">
              <img
                className="dark:hidden h-12 text-center block"
                src={"/images/black-logo.png"}
                alt="Collectr"
              />
              <img
                className="hidden h-12 text-center dark:block"
                src={"/images/white-logo.png"}
                alt="Collectr"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className='sm:mt-0 mt-4'>
          <div className='flex flex-col justify-center'>
            <FieldSet>
              <FieldGroup>
                <Field className='flex flex-col items-center justify-center'>
                  <FieldLabel htmlFor='code' className='text-3xl flex flex-col items-center justify-center'>
                    Check your email
                  </FieldLabel>
                  <FieldDescription className='text-center'>We've sent a 6-character code to {email ? email : 'your email'}. The code expires shortly, so please enter it soon.</FieldDescription>

                  {codeResent && (
                    <Alert variant='success' className='[&>svg]:size-4'>
                      <CheckIcon />
                      <AlertTitle>Code re-sent successfully.</AlertTitle>
                    </Alert>
                  )}

                  <InputOTP maxLength={6} containerClassName='justify-center my-4' id='code' value={code} onChange={(value) => setCode(value)} onComplete={handleSubmit}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator className='text-muted-foreground' />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>

                  {error && <p className='text-destructive text-sm font-normal'>{error}</p>}

                  <Button className='w-full' type='submit' variant={'brandPrimary'}  onClick={handleSubmit} isLoading={startUserEmailAuthSession.isPending} disabled={startUserEmailAuthSession.isPending}>
                    Verify Code
                  </Button>

                  {manualRedirectUrl && (
                    <a href={manualRedirectUrl} target='_blank' rel='noopener noreferrer'>
                      <Button className='w-full' type='submit' variant={'outline'}  isLoading={startUserEmailAuthSession.isPending} disabled={startUserEmailAuthSession.isPending}>
                        If you haven't been redirected, click here
                        <ArrowUpRight />
                      </Button>
                    </a>
                  )}

                  {email && (
                    <FieldDescription className='flex flex-row items-center justify-center text-muted-foreground cursor-pointer'>
                      Didn't receive an email?
                      <Button onClick={handleResendCode} type='button' className='text-collectr-brand-primary px-1.5' variant={'link'} loaderClassName='ml-0' isLoading={sendEmailVerificationCode.isPending} disabled={sendEmailVerificationCode.isPending}>
                        Resend code
                      </Button>
                    </FieldDescription>
                  )}
                </Field>
              </FieldGroup>
            </FieldSet>
          </div>

          <Button className='w-full mt-8' variant={'ghost'} onClick={handleNavigateBack}>
            <ArrowleftIcon />
            Go back to login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyCodePage;
